import { useMemo, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, BarChart3, Check, RotateCcw, TrendingUp } from "lucide-react";

import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { IsoGrid, IsoIllustration, IsoStep } from "@/components/site/Iso";
import { RequestForm } from "@/components/site/RequestForm";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useT } from "@/lib/i18n-dict";
import { alternateLinks, localePath, normalizeLocale, SITE_URL } from "@/lib/i18n";
import {
  calculateRoi,
  DEFAULT_ROI_INDUSTRY,
  ROI_INDUSTRIES,
  ROI_SCENARIOS,
  roiAssumption,
  type RoiInputKey,
  type RoiInputs,
  type ScenarioKey,
} from "@/lib/roi-calculator";
import { cn } from "@/lib/utils";
import { localeMeta } from "@/lib/seo-head";

const TITLE = "ROI calculator for manufacturing planning software | factory.online";
const DESCRIPTION =
  "Estimate the payback from factory.online planning and scheduling by industry, with editable assumptions for planner time, errors, inventory, WIP and five-year ROI.";

const INPUT_GROUPS: {
  title: string;
  description: string;
  fields: { key: RoiInputKey; label: string; suffix?: string; step?: number; min?: number; max?: number }[];
}[] = [
  {
    title: "Planner productivity",
    description: "Time saved when schedules, material checks and replanning move out of spreadsheets.",
    fields: [
      { key: "hoursSavedWeekly", label: "Planner hours saved per week", suffix: "h", step: 1, min: 0, max: 240 },
      { key: "plannerHourlyCost", label: "Planner hourly cost", suffix: "€", step: 1, min: 0, max: 500 },
    ],
  },
  {
    title: "Avoided planning errors",
    description: "Late changes, missing materials, wrong versions and preventable production stops.",
    fields: [
      { key: "avoidedErrorsYearly", label: "Avoided errors per year", step: 1, min: 0, max: 500 },
      { key: "costPerError", label: "Average cost per error", suffix: "€", step: 100, min: 0, max: 250000 },
    ],
  },
  {
    title: "Working capital release",
    description: "Cash released by lowering excess inventory and work in progress without hurting service levels.",
    fields: [
      { key: "averageInventoryValue", label: "Average inventory value", suffix: "€", step: 1000, min: 0, max: 50000000 },
      { key: "inventoryReductionPct", label: "Inventory reduction", suffix: "%", step: 0.5, min: 0, max: 80 },
      { key: "averageWipValue", label: "Average WIP value", suffix: "€", step: 1000, min: 0, max: 50000000 },
      { key: "wipReductionPct", label: "WIP reduction", suffix: "%", step: 0.5, min: 0, max: 80 },
    ],
  },
  {
    title: "Program cost",
    description: "Implementation, subscription and yearly support assumptions used in the five-year view.",
    fields: [
      { key: "implementationCost", label: "Implementation cost", suffix: "€", step: 1000, min: 0, max: 1000000 },
      { key: "yearlySubscription", label: "Yearly subscription", suffix: "€", step: 500, min: 0, max: 1000000 },
      { key: "yearlyMaintenanceCost", label: "Yearly maintenance and support", suffix: "€", step: 500, min: 0, max: 1000000 },
    ],
  },
];

const OPERATIONAL_METRICS: { key: RoiInputKey; label: string }[] = [
  { key: "otdImprovementPct", label: "On-time delivery improvement" },
  { key: "utilizationImprovementPct", label: "Equipment utilization uplift" },
  { key: "forecastAccuracyImprovementPct", label: "Forecast accuracy improvement" },
];

export const Route = createFileRoute("/{-$locale}/roi-calculator")({
  staticData: { sitemap: true },
  loader: async ({ params }) => ({ locale: normalizeLocale((params as { locale?: string }).locale) }),
  head: ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const path = localePath(locale, "/roi-calculator");
    const meta = localeMeta(params, TITLE, DESCRIPTION);
    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE_URL}${path}` },
        { property: "og:locale", content: locale },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}${path}` }, ...alternateLinks("/roi-calculator")],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "factory.online ROI calculator",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description: DESCRIPTION,
            url: `${SITE_URL}${path}`,
          }),
        },
      ],
    };
  },
  component: RoiCalculatorPage,
  errorComponent: () => (
    <Section>
      <p role="alert" className="text-center text-muted-foreground">
        ROI calculator is temporarily unavailable. Please try again shortly.
      </p>
    </Section>
  ),
});

function RoiCalculatorPage() {
  const t = useT();
  const { locale } = Route.useLoaderData();
  const [industrySlug, setIndustrySlug] = useState(DEFAULT_ROI_INDUSTRY);
  const [scenario, setScenario] = useState<ScenarioKey>("expected");
  const [inputs, setInputs] = useState<RoiInputs>(() => ({ ...roiAssumption(DEFAULT_ROI_INDUSTRY).inputs }));

  const industry = roiAssumption(industrySlug);
  const results = useMemo(() => calculateRoi(inputs, scenario), [inputs, scenario]);
  const money = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }),
    [locale],
  );
  const number = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }), [locale]);
  const percent = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 }),
    [locale],
  );
  const maxTimelineValue = Math.max(...results.timeline.map((row) => Math.max(row.cost, row.benefit)), 1);
  const contactMessage = `${t("ROI calculator")}: ${t(industry.label)} · ${t(activeScenarioLabel(scenario))} · ${t("payback")}: ${results.paybackMonths ?? "—"} ${t("months")} · ${t("five-year net benefit")}: ${money.format(results.netBenefit)}.`;

  const updateInput = (key: RoiInputKey, value: number) => {
    setInputs((current) => ({ ...current, [key]: value }));
  };

  const chooseIndustry = (slug: string) => {
    const next = roiAssumption(slug);
    setIndustrySlug(next.slug);
    setInputs({ ...next.inputs });
  };

  const resetAssumptions = () => setInputs({ ...industry.inputs });

  return (
    <>
      <section className="relative overflow-hidden border-b border-border/70 py-16 md:py-24">
        <IsoGrid intensity="soft" />
        <div className="container-page relative grid items-center gap-10 lg:grid-cols-[1.05fr_.95fr]">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">{t("Manufacturing ROI calculator")}</p>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold text-balance-tight md:text-6xl">
              {t("Calculate the payback from better planning")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              {t(
                "Start with your industry, adjust the assumptions from the Excel model and see payback, five-year ROI, cash release and operational impact in one view.",
              )}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
              <GhostCta to="/solution-finder">Solution finder</GhostCta>
            </div>
          </Reveal>
          <Reveal variant="iso" delay={80}>
            <div className="relative mx-auto w-full max-w-xl">
              <IsoIllustration name="planner" alt={t("Isometric planning board")} className="w-full" parallax={false} />
              <div className="absolute bottom-4 left-4 right-4 border border-border bg-card/95 p-4 shadow-[var(--shadow-lift)] backdrop-blur">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-muted-foreground">{t("Expected payback")}</span>
                  <span className="font-display text-2xl font-bold text-primary">
                    {formatMonths(results.paybackMonths, t, number)}
                  </span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-primary/15">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${paybackProgress(results.paybackMonths)}%` }} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Section>
        <div className="grid items-start gap-10 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,.9fr)]">
          <div className="grid gap-10">
            <ChoiceSection n="1" title={t("Choose your industry")} hint={t("Assumptions are prefilled for the selected manufacturing profile.")}> 
              <div className="grid gap-3 sm:grid-cols-2">
                {ROI_INDUSTRIES.map((item) => (
                  <ChoiceButton
                    key={item.slug}
                    selected={industrySlug === item.slug}
                    onClick={() => chooseIndustry(item.slug)}
                    title={t(item.label)}
                    description={t(item.profile)}
                  />
                ))}
              </div>
            </ChoiceSection>

            <ChoiceSection n="2" title={t("Pick a scenario")} hint={t("The scenario adjusts impact assumptions, not your cost inputs.")}> 
              <Tabs value={scenario} onValueChange={(value) => setScenario(value as ScenarioKey)} dir="ltr">
                <TabsList className="h-auto flex-wrap justify-start rounded-md bg-muted p-1">
                  {ROI_SCENARIOS.map((item) => (
                    <TabsTrigger key={item.key} value={item.key} className="min-h-9 whitespace-normal rounded-sm px-4 text-start">
                      {t(item.label)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                {ROI_SCENARIOS.map((item) => (
                  <div key={item.key} className={cn("border border-border p-4", scenario === item.key && "border-primary bg-primary/10")}> 
                    <p className="font-display text-base font-semibold">{t(item.label)}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{t(item.description)}</p>
                  </div>
                ))}
              </div>
            </ChoiceSection>

            <ChoiceSection n="3" title={t("Adjust assumptions")} hint={t("Values stay in your browser only and are not saved.")}> 
              <div className="grid gap-5">
                {INPUT_GROUPS.map((group) => (
                  <div key={group.title} className="border border-border bg-card p-5 md:p-6">
                    <div className="mb-5">
                      <h3 className="font-display text-xl font-bold">{t(group.title)}</h3>
                      <p className="mt-2 text-sm text-muted-foreground">{t(group.description)}</p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {group.fields.map((field) => (
                        <NumberField
                          key={field.key}
                          label={t(field.label)}
                          suffix={field.suffix}
                          value={inputs[field.key]}
                          min={field.min ?? 0}
                          max={field.max}
                          step={field.step ?? 1}
                          onChange={(value) => updateInput(field.key, value)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                <div className="border border-border bg-card p-5 md:p-6">
                  <h3 className="font-display text-xl font-bold">{t("Operational lift")}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t("These metrics do not change cash flow directly; they show the DuPont-style operating levers behind the ROI.")}
                  </p>
                  <div className="mt-5 grid gap-4 sm:grid-cols-3">
                    {OPERATIONAL_METRICS.map((field) => (
                      <NumberField
                        key={field.key}
                        label={t(field.label)}
                        suffix="%"
                        value={inputs[field.key]}
                        min={0}
                        max={80}
                        step={0.5}
                        onChange={(value) => updateInput(field.key, value)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </ChoiceSection>
          </div>

          <aside className="xl:sticky xl:top-24">
            <div className="border border-border bg-card p-6 shadow-[var(--shadow-lift)] md:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">{t("ROI summary")}</p>
                  <h2 className="mt-2 font-display text-2xl font-bold">{t(industry.label)}</h2>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={resetAssumptions} aria-label={t("Reset assumptions")}> 
                  <RotateCcw aria-hidden />
                </Button>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t(industry.profile)}</p>
              <ul className="mt-5 grid gap-2">
                {industry.focus.map((item) => (
                  <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                    {t(item)}
                  </li>
                ))}
              </ul>

              <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                <MetricCard label={t("Payback period")} value={formatMonths(results.paybackMonths, t, number)} />
                <MetricCard label={t("Five-year net benefit")} value={money.format(results.netBenefit)} />
                <MetricCard label={t("ROI multiple")} value={`${percent.format(results.roiMultiple)}×`} />
                <MetricCard label={t("Recurring yearly benefit")} value={money.format(results.recurringBenefits)} />
              </div>

              <div className="mt-7 border-t border-border pt-6">
                <h3 className="font-display text-lg font-semibold">{t("Benefit breakdown")}</h3>
                <div className="mt-4 grid gap-3">
                  <BreakdownRow label={t("Planner productivity")} value={results.laborSaving} total={results.totalBenefit} money={money} />
                  <BreakdownRow label={t("Avoided errors")} value={results.errorSaving} total={results.totalBenefit} money={money} />
                  <BreakdownRow label={t("Inventory release")} value={results.inventoryRelease} total={results.totalBenefit} money={money} />
                  <BreakdownRow label={t("WIP release")} value={results.wipRelease} total={results.totalBenefit} money={money} />
                </div>
              </div>

              <Button type="button" className="mt-7 w-full" asChild>
                <a href="#roi-contact">
                  {t("Validate this ROI")}
                  <ArrowRight aria-hidden />
                </a>
              </Button>
            </div>
          </aside>
        </div>
      </Section>

      <Section className="bg-surface/40">
        <SectionHeading
          eyebrow={t("Five-year view")}
          title={t("Costs, benefits and cumulative savings")}
          lead={t("The model follows the uploaded Excel logic: one-off working-capital benefits in year one, recurring labor and error savings in later years.")}
        />
        <div className="mt-10 overflow-x-auto border border-border bg-card">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-semibold">{t("Year")}</th>
                <th className="px-4 py-3 font-semibold">{t("Cost")}</th>
                <th className="px-4 py-3 font-semibold">{t("Benefit")}</th>
                <th className="px-4 py-3 font-semibold">{t("Annual saving")}</th>
                <th className="px-4 py-3 font-semibold">{t("Cumulative saving")}</th>
                <th className="px-4 py-3 font-semibold">{t("Timeline")}</th>
              </tr>
            </thead>
            <tbody>
              {results.timeline.map((row) => (
                <tr key={row.year} className="border-b border-border/70 last:border-b-0">
                  <td className="px-4 py-4 font-semibold">
                    {t("Year")} {row.year}
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">{money.format(row.cost)}</td>
                  <td className="px-4 py-4 text-muted-foreground">{money.format(row.benefit)}</td>
                  <td className={cn("px-4 py-4 font-semibold", row.saving >= 0 ? "text-primary" : "text-muted-foreground")}>{money.format(row.saving)}</td>
                  <td className={cn("px-4 py-4 font-semibold", row.cumulativeSaving >= 0 ? "text-primary" : "text-muted-foreground")}>
                    {money.format(row.cumulativeSaving)}
                  </td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <TimelineBar label={t("Cost")} value={row.cost} max={maxTimelineValue} />
                      <TimelineBar label={t("Benefit")} value={row.benefit} max={maxTimelineValue} highlight />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
          <Reveal>
            <SectionHeading
              eyebrow={t("DuPont-style impact bridge")}
              title={t("How operational changes become financial return")}
              lead={t("The calculator separates profit impact, cash release and asset productivity so the result is easier to challenge with finance and operations.")}
            />
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            <ImpactCard icon="trend" label={t("Profitability")} value={money.format(results.recurringBenefits)} text={t("Lower planning effort and fewer preventable errors improve operating profit every year.")} />
            <ImpactCard icon="cash" label={t("Working capital")} value={money.format(results.oneOffBenefits)} text={t("Less excess inventory and WIP releases cash in the first year of the program.")} />
            <ImpactCard icon="flow" label={t("Delivery reliability")} value={`+${percent.format(results.scenarioAdjusted.otdImprovementPct)}%`} text={t("Cleaner schedules improve promise dates, dispatch rhythm and customer trust.")} />
            <ImpactCard icon="assets" label={t("Asset productivity")} value={`+${percent.format(results.scenarioAdjusted.utilizationImprovementPct)}%`} text={t("Better sequencing increases usable capacity before you buy more equipment.")} />
          </div>
        </div>
      </Section>

      <Section className="bg-surface" id="roi-contact">
        <div className="mx-auto max-w-4xl">
          <SectionHeading
            eyebrow={t("Validate on your own data")}
            title={t("Turn this estimate into a rollout case")}
            lead={t("Send the scenario and we will review assumptions, implementation scope and the first factory.online modules to switch on.")}
          />
          <div className="mt-10">
            <RequestForm
              key={`${industrySlug}-${scenario}`}
              kind="demo"
              withMessage
              defaultIndustry={industry.label}
              hideIndustry
              defaultMessage={contactMessage}
              submitLabel={t("Send my ROI scenario")}
              successTitle={t("ROI scenario received")}
              successText={t("A manufacturing consultant will review the assumptions and contact you shortly.")}
            />
          </div>
        </div>
      </Section>
    </>
  );
}

function activeScenarioLabel(key: ScenarioKey): string {
  return ROI_SCENARIOS.find((item) => item.key === key)?.label ?? "Expected";
}

function formatMonths(value: number | null, t: (text: string) => string, number: Intl.NumberFormat) {
  if (value === null) return t("Not reached");
  return `${number.format(value)} ${t("months")}`;
}

function paybackProgress(value: number | null) {
  if (value === null) return 18;
  return Math.max(10, Math.min(100, Math.round((1 - Math.min(value, 60) / 60) * 100)));
}

function ChoiceSection({
  n,
  title,
  hint,
  children,
}: {
  n: string;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-5 flex items-center gap-4">
        <IsoStep n={n} />
        <h2 className="font-display text-2xl font-bold">{title}</h2>
      </div>
      {hint ? <p className="mb-5 text-muted-foreground">{hint}</p> : null}
      {children}
    </section>
  );
}

function ChoiceButton({ selected, onClick, title, description }: { selected: boolean; onClick: () => void; title: string; description: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "h-auto min-h-28 w-full justify-between whitespace-normal rounded-md p-4 text-start",
        selected && "border-primary bg-primary/10 text-foreground ring-1 ring-primary",
      )}
    >
      <span>
        <span className="block text-base font-semibold">{title}</span>
        <span className="mt-2 block text-sm font-normal leading-relaxed text-muted-foreground">{description}</span>
      </span>
      {selected ? <Check className="size-5 shrink-0 text-primary" aria-hidden /> : <ArrowRight className="size-5 shrink-0 text-muted-foreground" aria-hidden />}
    </Button>
  );
}

function NumberField({
  label,
  suffix,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  suffix?: string | undefined;
  value: number;
  min: number;
  max?: number | undefined;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label className="text-sm font-semibold text-muted-foreground">
        {label}
      </Label>
      <div className="relative">
        <Input
          aria-label={label}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
          className={cn("h-11 bg-background text-base", suffix && "pe-12")}
        />
        {suffix ? <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">{suffix}</span> : null}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border/70 bg-background p-4">
      <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold text-primary">{value}</p>
    </div>
  );
}

function BreakdownRow({ label, value, total, money }: { label: string; value: number; total: number; money: Intl.NumberFormat }) {
  const width = total > 0 ? Math.max(4, Math.round((value / total) * 100)) : 4;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{money.format(value)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-primary/15">
        <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(width, 100)}%` }} />
      </div>
    </div>
  );
}

function TimelineBar({ label, value, max, highlight }: { label: string; value: number; max: number; highlight?: boolean }) {
  const width = Math.max(3, Math.round((value / max) * 100));
  return (
    <div className="grid grid-cols-[4.5rem_1fr] items-center gap-2 text-xs text-muted-foreground">
      <span>{label}</span>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", highlight ? "bg-primary" : "bg-muted-foreground/35")} style={{ width: `${Math.min(width, 100)}%` }} />
      </div>
    </div>
  );
}

function ImpactCard({ icon, label, value, text }: { icon: "trend" | "cash" | "flow" | "assets"; label: string; value: string; text: string }) {
  const Icon = icon === "trend" ? TrendingUp : icon === "cash" ? BarChart3 : icon === "flow" ? ArrowRight : Check;
  return (
    <Reveal className="border border-border bg-card p-6">
      <div className="flex size-11 items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary">
        <Icon className="size-5" aria-hidden />
      </div>
      <p className="mt-5 text-sm font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold">{value}</p>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{text}</p>
    </Reveal>
  );
}